import { AgTechDataAction, DataActionResults } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechApiEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests";
import { useAgTechHttpSubmitEntityRequest, useAgTechHttpPostRequest, useAgTechHttpPostEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import _ from "lodash";
import { SaleLot, ReorderLotSaleOrderDTO, SaleLotOrderModificationGridEntity, isLotInvoiced } from "./LotData";
import { LotPurchaseWriteDTO } from "./purchases/LotPurchaseDataActions";
import { useCurrentSale } from "../sales/SaleDataStore";
import { LotDataGetLotSplitSummaryQuery, LotDataGetLotsQuery } from "./LotDataQueries";
import { useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { InvoiceDataGetUnprocessedSalesQuery } from "../invoicing/InvoicingDataQueries";
import { BidderDataGetSaleBiddersQuery } from "../bidders/BidderDataQueries";

/// Lot Actions

export const useLotDataWriteLotAction = (): AgTechDataAction<LotPurchaseWriteDTO, SaleLot, AgTechIndex<SaleLot>> => {
    const currentSale = useCurrentSale();

    const lotsQuery = useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: { saleId: currentSale.id }
    });

    const lotSubmissionPostRequest = useAgTechHttpSubmitEntityRequest<SaleLot>();

    return {
        name: 'WriteLot',
        query: lotsQuery,
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.id ? 'Updating lot...' : 'Creating lot...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.id ? 'updated' : 'created'} lot`
        }),
        validate: async props => {
            props.validation.failWithWarningIf(props.submittedEntity.lotNumber === '', 'Please specify a lot number for this lot.');
            
            const existingLots = Object.values(props.context);

            const lotWithSameLotNumber = existingLots.find(lot => {
                return lot.lotNumber === props.submittedEntity.lotNumber &&
                       lot.id !== props.submittedEntity.id
            });

            props.validation.failWithWarningIf(
                lotWithSameLotNumber !== undefined,
                'A lot with lot number ' + props.submittedEntity.lotNumber + ' already exists. Please specify a different lot number.'
            );

            const lotWithSameSaleOrder = existingLots.find(lot => {
                return lot.saleOrder === props.submittedEntity.saleOrder &&
                       lot.id !== props.submittedEntity.id
            });

            props.validation.failWithWarningIf(
                lotWithSameSaleOrder !== undefined,
                'A lot with sale order ' + props.submittedEntity.saleOrder + ' already exists. Please specify a different sale order.'
            );
        },
        action: async props => {
            if (props.submittedEntity.groupSize === 0)
                props.submittedEntity.groupSize = undefined;
                
            return await lotSubmissionPostRequest.post({
                path: 'Lot',
                postedData: {
                    ...props.submittedEntity,
                    saleId: props.submittedEntity.saleId !== 0 ? props.submittedEntity.saleId : currentSale.id
                }
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id },
                update: lots => {
                    return {
                        ...lots,
                        [props.responseData.id]: props.responseData
                    };
                }
            });
        }
    }
}

export const useLotDataRemoveLotAction = (): AgTechDataAction<SaleLot, any> => {
    const currentSale = useCurrentSale();
    const lotDeleteRequest = useAgTechHttpPostRequest<number, boolean>();

    return {
        name: 'DeleteLot',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting lot...',
            actionConfirmationMessage: `Successfully deleted lot`
        }),
        validate: async props => {
            return props.validation.failWithWarningIf(
                isLotInvoiced(props.submittedEntity),
                `Lot ${props.submittedEntity.lotNumber} has been invoiced and cannot be deleted`);
        },
        confirm: async props => {
            const lot = props.submittedEntity;
            const hasLotBeenPurchased = lot.bidderNumber && lot.purchasePrice;

            const lotDeleteConfirmationHeader = hasLotBeenPurchased
                ? `Lot "${lot.lotNumber}" is marked as purchased by bidder "${lot.bidderNumber}", removing this lot will also remove this purchase`
                : `Are you sure you wish to remove lot "${lot.lotNumber}" from the lots in this sale?`;
            
            const lotDeleteConfirmationDetails = hasLotBeenPurchased
                ? "Are you sure you want to remove this lot?"
                : undefined;

            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: lotDeleteConfirmationHeader,
                    details: lotDeleteConfirmationDetails,
                }
            });
        },
        action: async props => {
            return await lotDeleteRequest.post({
                path: 'Lot/Delete/' + props.submittedEntity.id,
                postedData: props.submittedEntity.id,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id },
                update: lots => {
                    const updatedLots = Object.values(lots)
                        .filter(cat => cat.id !== props.submittedEntity.id);

                    return Index(updatedLots, e => e.id);
                }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });
            
            props.appContext.popups.closeAll();
        }
    }
}

/// Lot Import Actions

export const useLotDataImportLotsAction = (): AgTechDataAction<SaleLot[], SaleLot[]> => {
    const currentSale = useCurrentSale();
    const lotImportPostRequest = useAgTechHttpPostEntityRequest<SaleLot[]>();

    return {
        name: 'ImportLots',
        getConfiguration: props => ({
            actionExecutionMessage: 'Importing lots...',
            actionConfirmationMessage: `Successfully imported ${props.submittedEntity.length} ${props.submittedEntity.length > 1 ? 'lots' : 'lot'}`
        }),
        validate: async props => {
            props.validation.failWithWarningIf(props.submittedEntity.length === 0, 'Please select one or more lots to import');
        },
        action: async props => {
            const lotsToImport = props.submittedEntity;
            lotsToImport.forEach(lt => { lt.saleId = currentSale.id; });

            return await lotImportPostRequest.post({
                path: 'Lot/Import/' + currentSale.id,
                postedData: lotsToImport
            });
        },
        onSuccess: async props => {
            props.invalidateQuery({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

/// Lot Split Actions

export type LotSplitActionData = {
    lotId: number,
    bidderPercentages: { bidderId?: number, contactId: number, percentage: number }[]
}

export const useLotDataSplitLotAction = (): AgTechDataAction<LotSplitActionData, AgTechApiEntityResponse<SaleLot>> => {
    const currentSale = useCurrentSale();
    const lotSplitPostRequest = useAgTechHttpPostRequest<LotSplitActionData & { saleId: number }, any>();

    return {
        name: 'SplitLot',
        getConfiguration: props => ({
            actionExecutionMessage: 'Splitting lot...',
            actionConfirmationMessage: 'Lot splitting was successful'
        }),
        validate: async props => {
            props.validation.failWithWarningIf(
                props.submittedEntity.bidderPercentages.length === 1,
                'Please select one or more splits for this lot');

            props.validation.failWithWarningIf(
                _.sumBy(props.submittedEntity.bidderPercentages, bp => bp.percentage) !== 1,
                'Please make sure the total percentage of all splits is 100%');
        },
        action: async props => {
            return await lotSplitPostRequest.post({
                path: 'Lot/Split',
                postedData: {
                    ...props.submittedEntity,
                    saleId: currentSale.id
                }
            });
        },
        onSuccess: async props => {
            // TODO: Invalidate queries
            props.invalidateQuery({
                    query: LotDataGetLotSplitSummaryQuery,
                    queryContext: props.responseData.data
                });
            
            props.invalidateQuery({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: {
                    saleId: currentSale.id
                }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

/// Lot Invoicing Actions

export const useLotDataMarkLotAsInvoicedAction = (): AgTechDataAction<{ lotIds: number[] }, any> => {

    return {
        name: 'MarkLotsAsInvoiced',
        action: async props => {

            return DataActionResults.Success('');
        }
    }
}

export const useLotDataWriteSaleReorderAction = (): AgTechDataAction<{ id: number, saleOrder: number, wasChanged: boolean }[], boolean> => {
    const currentSale = useCurrentSale();
    const lotReorderPostRequest = useAgTechHttpPostRequest<ReorderLotSaleOrderDTO[], boolean>();

    return {
        name: "ReorderSale",
        getConfiguration: () => ({
            actionExecutionMessage: 'Reordering lots...',
            actionConfirmationMessage: 'Successfully reordered lots'
        }),
        validate: async props => {
            props.validation.failWithWarningIf(props.submittedEntity.find(e => e.saleOrder === 0) !== undefined, 'One or more of the sale orders are currently set to 0');
        },
        action: async props => {
            const lotsWithSaleOrderUpdated = props.submittedEntity.filter(e => e.wasChanged)
                .map<ReorderLotSaleOrderDTO>((c, i) => ({
                    lotId: c.id,
                    newSaleOrder: c.saleOrder
                }));
            
            return await lotReorderPostRequest.post({
                path: 'Lot/Reorder/' + currentSale.id,
                postedData: lotsWithSaleOrderUpdated
            });
        },
        onSuccess: async props => {
            props.invalidateQuery({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    };
};